import { MutationFetcher } from 'swr/mutation';

import { CreateWorkflowRequest } from 'MODULES/workflows/types/mutations';
import { ProcessorSuggestionsResponse } from 'MODULES/workflows/types/queries/block-processors-suggestions';
import {
  BlockSchemaInputOptions,
  BlockSchemaResponse,
} from 'MODULES/workflows/types/queries/block-schema';
import { ToolboxSuggestionsResponse } from 'MODULES/workflows/types/queries/toolbox-suggestions';
import {
  WorkflowRevision,
  WorkflowRevisionChainWithLatestResponse,
} from 'MODULES/workflows/types/queries/workflows-sources';
import {
  WorkflowSourceChecks,
  PublishedWorkflowSource,
  WorkflowSource,
} from 'MODULES/workflows/types/workflowSource';
import { workflowBuilderApiUrl } from 'SRC/config';
import { del, get, post } from 'SRC/fetch/fetch';

export const WORKFLOWS_KEY = '/workflows-v1';
export const getWorkflows = (searchParams?: string) => {
  return get<WorkflowRevisionChainWithLatestResponse>(
    workflowBuilderApiUrl +
      WORKFLOWS_KEY +
      (searchParams ? `?${searchParams}` : ''),
    {},
    { auth: true },
  );
};

export const postCreateWorkflow: MutationFetcher<
  WorkflowSource,
  string,
  CreateWorkflowRequest
> = (_, { arg: body }) => {
  return post<WorkflowSource>(
    workflowBuilderApiUrl + WORKFLOWS_KEY,
    { body: JSON.stringify(body) },
    {
      auth: true,
    },
  );
};

export const WORKFLOW_REVISIONS_KEY = '/workflows-v1/:id/revisions';
export const getWorkflowRevisions = (id: string) => {
  return get<{ data: WorkflowRevision[] }>(
    workflowBuilderApiUrl + `/workflows-v1/${id}/revisions`,
    {},
    { auth: true },
  );
};

export const WORKFLOW_VERSION_KEY = '/workflows-v1/:id/revisions/:version';
export const getWorkflowVersion = ({
  id,
  version,
}: {
  id: string;
  version: number;
}) => {
  return get<WorkflowSource>(
    workflowBuilderApiUrl + `/workflows-v1/${id}/revisions/${version}`,
    {},
    { auth: true },
  );
};

export const WORKFLOW_TOOLBOX_SUGGESTIONS_KEY =
  '/workflows-v1/:workflow-id/revisions/:workflow-version/toolbox-suggestions';
export const getWorkflowToolboxSuggestions = (
  workflowId: string,
  workflowVersion: number,
) => {
  return get<ToolboxSuggestionsResponse>(
    workflowBuilderApiUrl +
      WORKFLOW_TOOLBOX_SUGGESTIONS_KEY.replace(
        ':workflow-id',
        workflowId,
      ).replace(':workflow-version', workflowVersion.toString()),
    {},
    { auth: true },
  );
};

export const DELETE_WORKFLOW_KEY = '/workflows-v1/:id';
export const deleteWorkflow: MutationFetcher<void, string, { id: string }> = (
  _,
  { arg: { id } },
) => {
  return del<void>(
    workflowBuilderApiUrl + '/workflows-v1/' + id,
    {},
    { auth: true },
  );
};

export const DUPLICATE_WORKFLOW_KEY =
  '/workflows-v1/:id/revisions/:version/duplicate';
export const postDuplicateWorkflow: MutationFetcher<
  WorkflowSource,
  string,
  { id: string; version: number }
> = (_, { arg: { id, version } }) => {
  return post<WorkflowSource>(
    workflowBuilderApiUrl +
      `/workflows-v1/${id}/revisions/${version}/duplicate`,
    {},
    { auth: true },
  );
};

export const ARCHIVE_WORKFLOW_KEY = '/workflows-v1/:id/archive';
export const postArchiveWorkflow: MutationFetcher<
  WorkflowSource,
  string,
  { id: string }
> = (_, { arg: { id } }) => {
  return post<WorkflowSource>(
    workflowBuilderApiUrl + `/workflows-v1/${id}/archive`,
    {},
    { auth: true },
  );
};

export const ACTION_SCHEMA_KEY =
  '/workflows-v1/schemas/:app-id/action/:action-id/:version';
export const getActionSchema = ({
  applicationId,
  actionId,
  version,
}: {
  applicationId: string;
  actionId: string;
  version: string;
}) => {
  return get<BlockSchemaResponse>(
    workflowBuilderApiUrl +
      `/workflows-v1/schemas/${applicationId}/action/${actionId}/${version}`,
    {},
    { auth: true },
  );
};

export const FIELD_DYNAMIC_INPUT_OPTIONS_KEY =
  '/workflows-v1/:id/revisions/:version/input-options';
export const getFieldDynamicInputOptions = ({
  id,
  version,
  searchParams,
}: {
  id: string;
  version: number;
  searchParams?: string;
}) => {
  return get<BlockSchemaInputOptions>(
    workflowBuilderApiUrl +
      `/workflows-v1/${id}/revisions/${version}/input-options${
        searchParams ? `?${searchParams}` : ''
      }`,
    {},
    { auth: true },
  );
};

export const PROCESSOR_SUGGESTIONS_KEY =
  '/workflows-v1/:id/revisions/:version/processor-suggestions';
export const getProcessorSuggestions = ({
  id,
  version,
  searchParams,
}: {
  id: string;
  version: number;
  searchParams?: string;
}) => {
  return get<ProcessorSuggestionsResponse>(
    workflowBuilderApiUrl +
      `/workflows-v1/${id}/revisions/${version}/processor-suggestions${
        searchParams ? `?${searchParams}` : ''
      }`,
    {},
    { auth: true },
  );
};

export const WORKFLOW_VALIDATION_KEY = '/workflows-v1/:id/validate';
export const getWorkflowValidation = (id: string) => {
  return get<{
    results: WorkflowSourceChecks;
  }>(
    workflowBuilderApiUrl + `/workflows-v1/${id}/validate`,
    {},
    { auth: true },
  );
};

export const PUBLISH_WORKFLOW_KEY = '/workflows-v1/:id/publish';
export const postPublishWorkflow: MutationFetcher<
  PublishedWorkflowSource,
  string,
  { id: string }
> = (_, { arg: { id } }) => {
  return post<PublishedWorkflowSource>(
    workflowBuilderApiUrl + `/workflows-v1/${id}/publish`,
    {},
    { auth: true },
  );
};
